import toast from "react-hot-toast";

import i18n from "@/i18n/config";
import type { CertificateCheckoutCreateResponse } from "@/services/certificatePaymentsService";

/**
 * Did this checkout settle without any payment step at all?
 *
 * True for the dev/zero-amount bypass, where the server marks a checkout paid
 * at creation and returns no redirect.
 *
 * THE EXCLUSIONS BELOW ARE LOAD-BEARING, and each one is a way a consumer
 * could otherwise get a request for free. A null `redirectUrl` does NOT mean
 * "already paid" — it means "this provider has nothing to redirect to", and
 * three different situations share that shape:
 *
 *   - BANK TRANSFER: pending, null redirect. The consumer has not transferred
 *     anything yet and has uploaded no receipt.
 *   - MERCADO PAGO: pending, null redirect. The card has not been entered yet;
 *     Checkout Bricks tokenises in the browser and the order is created from
 *     the token afterwards.
 *   - the real bypass: already `paid`.
 *
 * So the test is the STATUS, and a pending non-redirecting provider is
 * explicitly not a bypass. Mistaking either for one makes the caller create
 * the request immediately, before any money has moved.
 */
export function isCheckoutAutoPaidBypass(created: CertificateCheckoutCreateResponse): boolean {
  if (created.status === "paid") return true;
  /**
   * KEYED ON THE METHOD, not on the presence of the instructions object. A
   * RESUMED bank-transfer checkout carries no `bankTransfer` payload — the
   * instructions page fetches those itself — so testing for it read a pending
   * transfer as an auto-paid bypass and would have created the request for
   * free. The method is the fact; the payload is a convenience.
   */
  if (created.paymentMethod === "bank_transfer" || created.bankTransfer) return false;
  if (created.paymentMethod === "mercadopago") return false;
  return created.redirectUrl == null || created.redirectUrl === "";
}

/**
 * Dev/staging: checkout may be pre-marked paid with no redirect. Surface a short notice.
 */
export function toastIfCheckoutAutoPaidBypass(created: CertificateCheckoutCreateResponse): void {
  if (isCheckoutAutoPaidBypass(created)) {
    if (created.amountMinor > 0) {
      toast(i18n.t("requests.user.paymentSkippedDevAutoApproved"), { duration: 7000 });
    }
  }
}

/**
 * Hand the visitor off to the dLocal checkout page.
 *
 * EMITS NO ANALYTICS. `payment_initiated` was removed here on 2026-10-09,
 * along with the server-side `request_paid`: a payment is not the conversion
 * this business measures. The conversion is the visitor committing to an RFQ,
 * which happens far earlier and is `rfq_first_message_sent`.
 */
export function redirectToCertificateCheckout(
  checkoutRow: CertificateCheckoutCreateResponse,
  _context: { serviceType: string },
): void {
  /**
   * Bank transfer has no redirect to follow — the money moves in the
   * consumer's own banking app. Send them to our instructions-and-upload page
   * instead; that page fetches the account details itself, so a refresh or a
   * return visit renders the same thing.
   */
  if (checkoutRow.paymentMethod === "bank_transfer" || checkoutRow.bankTransfer) {
    // Full assign rather than a router push: this mirrors the dLocal branch's
    // hand-off semantics, so the wizard is left in both cases and a back
    // button cannot re-submit a checkout that already exists. The instructions
    // are NOT carried in history state — a full load discards it — the target
    // page fetches them instead.
    window.location.assign(
      `/pay/bank-transfer?checkout_id=${encodeURIComponent(checkoutRow.checkoutId)}`,
    );
    return;
  }

  /**
   * Mercado Pago has no redirect either — the card is entered on OUR page,
   * inside Mercado Pago's iframes. Send the consumer to the Brick page, which
   * loads the checkout by id so a refresh or a return visit renders the same
   * thing.
   */
  if (checkoutRow.paymentMethod === "mercadopago") {
    window.location.assign(
      `/pay/mercadopago?checkout_id=${encodeURIComponent(checkoutRow.checkoutId)}`,
    );
    return;
  }

  if (checkoutRow.redirectUrl == null || checkoutRow.redirectUrl === "") return;
  window.location.assign(checkoutRow.redirectUrl);
}
